import { CASE_STUDIES } from "@Pages/List/Desktop/constants";
import { pageAsset } from "@Utils/getStaticAssetPath";

/**
 * The cards for both rails on /list — the partner quotes and the case studies.
 *
 * Split out of `Testimonials.tsx` when the one combined section became two, so
 * that section and `CaseStudies` can each take the list it needs without either
 * owning the other's data.
 */
// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("ListWithUsV2");

// Illustrated avatars, deliberately NOT photographs.
//
// There are no real headshots for these three. `assets/avatar-1..3.png` are stock
// PHOTOS, and one of those beside "Linsey Cullen, Head of Customer Experience, Fresh"
// reads as a claim about what a named partner at a named company looks like — and the
// one that fit the layout was a man. A drawing makes no such claim, so all three come
// from the illustrated set, matched loosely to the name's presentation.
const avatarLinsey = asset("avatars/face-03.png");
const avatarBo = asset("avatars/face-01.png");
const avatarDavid = asset("testimonial-avatar-1.png");
/**
 * The case-study covers — the supplied "amber | X" key visuals, in CASE_STUDIES order.
 *
 * ⚠️  THESE REPLACE A TWO-PART COMPOSITION, and the parts are gone with it. The card
 * used to build its hero from a pastel gradient tile plus a white PNG lockup laid over
 * it and `invert(1)`-ed for contrast — a workaround for there being no dark lockup on
 * the bucket, and for white measuring 1.0:1 to 1.5:1 on those pale grounds. These are
 * finished artwork with the lockup already set into the scene, so the ground, the
 * lockup, the per-brand sizing table that normalised the four "amber" wordmarks, and
 * the invert are all no longer needed. `assets/lockups/*-white.png` and the
 * `tile-gradient*.jpg` grounds stay on the bucket — other bands still use the tiles.
 *
 * Cropped to the hero's own 394:230 on the way in rather than at 16:9 as supplied, so
 * the shipped file carries no pixels the card would crop away. The crop is centred and
 * takes 1.8% off each side, which no lockup in this set comes near.
 *
 * All four are the photography set now. Londonist arrived a batch later than the other
 * three — its slot held the earlier glass-and-stone render in between — so if an older
 * checkout shows one CGI card among three photographs, that is why.
 *
 * Londonist encodes ~2x the others (147KB against 69-87) because its frame is stone
 * grain and foliage rather than a flat painted wall. That is detail, not encoder
 * slack: at q74 it still weighs 118KB. The whole set uses one encode — 1182x690,
 * q82, progressive — so the difference stays a property of the artwork.
 */
const COVERS = [
  asset("case-studies/londonist.jpg"),
  asset("case-studies/june.jpg"),
  asset("case-studies/zuma.jpg"),
  asset("case-studies/axo.jpg"),
];

/**
 * "Case studies & testimonials" — Figma node 2483:9540.
 *
 * Three cards on a scroll track with the design's dots + chevrons underneath.
 * At 1440px all three fit and the controls sit idle (disabled); below that the
 * track scrolls a card at a time, which is what the controls are for.
 */
/**
 * The real testimonials and case studies from the live /list page.
 *
 * Both sets are Tolgee keys upstream (`list.testimonials.pmg*`, `list.casestudies.study*`),
 * so the strings are not in this repo — these were read off the rendered live page. They
 * are hardcoded here because every other word on this page is: localisation for the whole
 * v2 set is one pass, not something to start in this one component.
 *
 * ⚠️  THE BRAND LOCKUP IS GONE, and deliberately.
 *
 * The card used to carry an "amber | londonist" mark. Live has four case-study lockups
 * — Londonist, June, Zuma, AXO — and three testimonials from Fresh, HFS and iQ, and it
 * pairs them by index (`PMG_TESTIMONIALS[i % 3]`). The two lists are unrelated, so that
 * pairing puts a Londonist logo above a quote signed "Head of Customer Experience,
 * Fresh". There is no lockup on S3 for Fresh, HFS or iQ (all 403), so the contradiction
 * cannot be fixed by picking a better image — only by not making the claim. The quote
 * now carries its own attribution and nothing else.
 */
export const TESTIMONIALS = [
  {
    id: "fresh",
    avatar: avatarLinsey,
    name: "Linsey Cullen",
    role: "list.v2.testimonials.role1",
    quote: "list.testimonials.pmg1.review",
  },
  {
    id: "hfs",
    avatar: avatarBo,
    name: "Bo Jia",
    role: "Homes for Students",
    quote: "list.testimonials.pmg2.review",
  },
  {
    id: "iq",
    avatar: avatarDavid,
    name: "David Seymour",
    role: "list.v2.testimonials.role3",
    quote: "list.testimonials.pmg3.review",
  },
];

/**
 * The four case studies. Titles and summaries are live's own copy — Tolgee keys
 * upstream (`list.casestudies.study*`), so read off the rendered page like the quotes.
 * Order matches `CASE_STUDIES`, which supplies the PDF each one links to.
 */
const STUDIES = [
  {
    title: "list.casestudies.study1.name",
    summary: "list.casestudies.study1.description",
  },
  {
    title: "list.casestudies.study2.name",
    summary: "list.casestudies.study2.description",
  },
  {
    title: "list.casestudies.study3.name",
    summary: "list.casestudies.study3.description",
  },
  {
    title: "list.casestudies.study4.name",
    summary: "list.casestudies.study4.description",
  },
];

/**
 * ⚠️  TWO KINDS OF CARD, INTERLEAVED — and they are separate on purpose.
 *
 * These were one card before: a person's quote with a brand lockup and a "Read Case
 * Study" link stacked into it. The two lists have nothing to do with each other, so
 * that pairing made claims it could not support — a Londonist lockup over a quote
 * signed "Head of Customer Experience, Fresh", and a link to Zuma's PDF under a quote
 * about iQ. Production has the same problem, plus one of its own: four studies against
 * three quotes means it cycles them, so the fourth card repeats the first quote.
 *
 * Split apart, each card carries one thing and says only what is true of it. It also
 * gives the AXO study a card of its own, which the paired version could not do without
 * inventing a fourth testimonial.
 *
 * The rail alternates while both lists last, then runs out the remaining studies:
 * quote, study, quote, study, quote, study, study.
 */
export type Card =
  | { kind: "quote"; id: string; avatar: string; name: string; role: string; quote: string }
  | {
      kind: "study";
      id: string;
      /** The supplied "amber | X" key visual — see COVERS. */
      cover: string;
      title: string;
      summary: string;
      pdf: string;
      nameKey: string;
    };

/**
 * ⚠️  TWO LISTS NOW, NOT ONE INTERLEAVED RAIL. These used to be woven together —
 * quote, study, quote, study, quote, study, study — into a single carousel under
 * the combined heading "Case Studies & Testimonials". They are separate sections
 * with separate headings now, so the interleave is gone: a rail is one kind of
 * thing, and the reader is told which before they start scrolling it.
 *
 * The card shapes are untouched, and so is the `Card` union above — the rail
 * still renders either kind, it is just never handed both at once.
 */

export const STUDY_CARDS: Card[] = STUDIES.map((study, i) => ({
  kind: "study",
  id: `study-${i}`,
  cover: COVERS[i % COVERS.length],
  title: study.title,
  summary: study.summary,
  pdf: CASE_STUDIES[i].pdf,
  nameKey: CASE_STUDIES[i].name,
}));

/** Tolerance for "is the track at one of its ends", in px. */
